import { useApi } from '../../lib/useApi.js';
import { PageHeader } from '../../app/PageHeader.jsx';
import {
  ErrorState,
  RbacMatrix,
  AlertCard,
  KpiHeroBand,
  KpiBandSkeleton,
  ChartSkeleton,
  GaugeCard,
  InlineScoreCell,
  bandTone,
  BrandedEmptyState,
  StaggerReveal,
} from '../../components/index.js';
import './admin.css';
import './rbacUsers.redesign.css';

export function RbacAdminPage() {
  const matrix = useApi('/rbac/matrix');

  if (matrix.loading) {
    return (
      <div className="rbac-redesign">
        <KpiBandSkeleton count={3} />
        <ChartSkeleton height={320} />
      </div>
    );
  }
  if (matrix.error) return <ErrorState error={matrix.error} />;

  const roleList = matrix.data?.data?.roles || [];
  const permList = matrix.data?.data?.permissions || [];
  const grants = matrix.data?.data?.grants || [];

  const rolePermissions = new Map();
  for (const role of roleList) {
    const grantedIds = new Set(
      grants
        .filter((grant) => grant.roleId === role.id && grant.granted)
        .map((grant) => grant.permissionId),
    );
    rolePermissions.set(role.id, grantedIds);
  }

  const hasData = roleList.length > 0 && permList.length > 0;

  // Grant coverage: granted cells / total role×permission cells. Derived purely
  // from the already-fetched matrix; no fabricated counts.
  const totalCells = roleList.length * permList.length;
  const grantedCells = grants.filter((g) => g.granted).length;
  const coveragePct = totalCells ? Math.round((grantedCells / totalCells) * 100) : 0;
  const coverageTone = bandTone(coveragePct, { good: 60, warn: 30 });

  // Per-role grant density (share of all permissions each role carries).
  const densityRows = roleList
    .map((role) => {
      const count = rolePermissions.get(role.id)?.size || 0;
      const pct = permList.length ? Math.round((count / permList.length) * 100) : 0;
      return { role, count, pct };
    })
    .sort((a, b) => b.pct - a.pct);

  const kpiItems = [
    {
      id: 'roles',
      label: 'Defined roles',
      value: roleList.length,
      footer: 'Backend-owned role definitions',
      status: 'blue',
    },
    {
      id: 'permissions',
      label: 'Permissions tracked',
      value: permList.length,
      footer: 'Distinct capability grants',
      status: 'blue',
    },
    {
      id: 'coverage',
      label: 'Grant coverage',
      value: coveragePct,
      unit: '%',
      footer: `${grantedCells} of ${totalCells} role/permission cells granted`,
      status: coverageTone,
      statusLabel: `${grantedCells} granted`,
    },
  ];

  return (
    <div className="rbac-redesign">
      {/* PRIME-SHELL-UX-001: single body page identity + relocated About. */}
      <PageHeader />
      {hasData ? (
        <KpiHeroBand
          eyebrow="Access governance"
          asOf={matrix.loadedAt}
          items={kpiItems}
          columns={3}
        />
      ) : null}

      {hasData ? (
        <StaggerReveal as="section" className="bento admin-bento">
          <div className="span-12 card">
            <div className="rbacusers-section-head">
              <div>
                <span className="eyebrow">Coverage &amp; density</span>
                <h2>Grant distribution by role</h2>
                <p className="muted">
                  Coverage is the share of all role/permission cells currently granted across the
                  backend-owned matrix. Each role bar shows how much of the permission catalog it
                  carries.
                </p>
              </div>
            </div>
            <div className="rbac-coverage-band">
              <div className="rbac-gauge-frame card compact">
                <GaugeCard
                  value={coveragePct}
                  min={0}
                  max={100}
                  unit="%"
                  label="Grant coverage"
                  sublabel={`${grantedCells} of ${totalCells} cells`}
                  tone={coverageTone}
                />
              </div>
              <div className="rbac-density-list" aria-label="Role grant density">
                {densityRows.map(({ role, count, pct }) => (
                  <div className="rbac-density-row" key={role.id}>
                    <div className="rbac-density-row__head">
                      <span className="rbac-density-row__name">{role.name || role.id}</span>
                      <div className="rbac-density-row__meta">
                        {role.classificationLabel ? (
                          <span className="chip outline sm">{role.classificationLabel}</span>
                        ) : null}
                        <span className="muted">
                          {count} of {permList.length} permissions
                        </span>
                      </div>
                    </div>
                    <InlineScoreCell
                      value={pct}
                      max={100}
                      suffix="%"
                      thresholds={{ good: 60, warn: 30 }}
                      ariaLabel={`${role.name || role.id}: ${pct}% of permissions granted`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </StaggerReveal>
      ) : null}

      <section className="bento admin-bento">
        <div className="span-12 card">
          {hasData ? (
            <RbacMatrix
              roles={roleList}
              permissions={permList}
              rolePermissions={rolePermissions}
              grants={grants}
              readOnly
            />
          ) : (
            <BrandedEmptyState
              icon="🛡️"
              eyebrow="Access governance"
              title="No RBAC data"
              description="No backend-owned roles or permissions are available to inspect yet."
            />
          )}
        </div>
        <div className="span-12">
          <AlertCard
            severity="blue"
            title="Read-only RBAC inspection"
            body="This matrix now reflects backend-owned grant truth. Tooltip interactions are inspect-only and do not emit permission-update toasts or mutate role state."
          />
        </div>
      </section>
    </div>
  );
}
